// Storage-Aggregat fuer die Dashboard-Card. Aggregiert pro Pool aus dem
// Detail-Store (storage-details):
//   - Total/Allocated bytes ueber alle Pools
//   - % belegt (gerundet)
//   - degraded count (Pool-Health != ONLINE)
//   - busiest_pool (groesster %-Wert) fuer Subtitle
//
// Kein eigener Abruf und kein Takt mehr (bis 02.11.2026: /v2-api/storage alle
// 60 s plus Monitore, zusaetzlich zum Detail-Store): die Zahlen werden aus
// getStorageDetails() + getStorageMonitors() + isEntityDisabled GERECHNET, die
// alle reaktiv sind -- ein Pool-Delta aus dem Protokoll, eine geaenderte
// Schwelle oder eine Stilllegung aendert die Karte sofort.
//
// Schwellen-Logik: Tim konfiguriert pro Pool seine warn_pct/crit_pct
// in den storage_monitors (Settings -> Storage-Monitore). Wir matchen
// pro Pool den Monitor-Eintrag (exakt auf Pool-Name). Faellt fuer einen
// Pool keiner, gilt der Default-Fallback (90% warn). Pool-Health !=
// ONLINE setzt unbedingt "problem" -- das ist nicht konfigurierbar.
//
// Kaltstart: bis der Disable-Stand da ist, zeigt die Karte die zuletzt
// gesicherte Summary (localStorage) -- sonst zaehlt ein bewusst stillgelegter
// Pool kurz als degraded (rote Karte blitzt auf). Gesichert wird mit dem
// Detail-Store (hoechstens alle 2 s).

import { entityDisablesLoaded, isEntityDisabled } from "./entity-disables.svelte";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { beiStorageGesichert, getStorageDetails, getStorageMonitors, startStorageDetails, type StorageMonitor } from "./storage-details.svelte";
import type { Storage } from "./types";
import { usablePct, usableSize, usableUsed } from "./zfs-capacity";

const DEFAULT_WARN_PCT = 90;
const DEFAULT_CRIT_PCT = 95;
const STORAGE_KEY = "storage";

export interface StorageSummary {
  status: "ok" | "warning" | "problem";
  pools_total: number;
  pools_degraded: number;
  bytes_total: number;
  bytes_used: number;
  pct_used: number;
  busiest_pool?: { name: string; pct: number };
}

let _started = false;
let _kalt = $state<StorageSummary | undefined>(undefined);
let _unsub: (() => void) | undefined;

function berechne(snap: Storage, monitors: StorageMonitor[]): StorageSummary {
  // Monitor-Map per Pool-Name. Nur Pool-Root-Eintraege (kein Pfad mit /)
  // zaehlen fuer die Pool-Level-Card. Sub-Dataset-Schwellen werden vom
  // Backend-Checker fuer Notifications evaluiert, nicht hier.
  const monitorByPool = new Map<string, StorageMonitor>();
  for (const m of monitors) {
    if (!m.dataset.includes("/")) monitorByPool.set(m.dataset, m);
  }

  let totalSize = 0;
  let totalAlloc = 0;
  let degraded = 0;
  let busiest: { name: string; pct: number } | undefined;
  let anyWarn = false;
  let anyCrit = false;

  for (const p of snap.pools) {
    // Nutzbare Kapazität, nicht roh: `zpool list` zählt Paritätsblöcke
    // mit und meldet für ein RAIDZ2 aus sechs Platten rund 50 % mehr,
    // als tatsächlich für Daten da ist. Siehe lib/zfs-capacity.ts.
    totalSize += usableSize(p);
    totalAlloc += usableUsed(p);
    // Bewusst stillgelegte Pools (domain "pool", Wartung/Platten-
    // Tausch) zaehlen nicht als degraded -- Dashboard-Karte bleibt gruen.
    const paused = isEntityDisabled("pool", `${p.host_id}:${p.name}`);
    if (p.health && p.health !== "ONLINE" && !paused) degraded++;
    if (usableSize(p) > 0) {
      // Auch die Schwellen laufen über die nutzbare Kapazität: wer
      // "warnen ab 80 %" einstellt, meint 80 % seines Speicherplatzes,
      // nicht 80 % inklusive Paritätsblöcken.
      const pct = usablePct(p);
      if (!busiest || pct > busiest.pct) {
        busiest = { name: p.name, pct };
      }
      const mon = monitorByPool.get(p.name);
      const warnPct = mon?.warn_pct ?? DEFAULT_WARN_PCT;
      const critPct = mon?.crit_pct ?? DEFAULT_CRIT_PCT;
      if (pct >= critPct) anyCrit = true;
      else if (pct >= warnPct) anyWarn = true;
    }
  }

  const pctUsed = totalSize > 0 ? Math.round((totalAlloc / totalSize) * 100) : 0;

  let status: StorageSummary["status"];
  if (degraded > 0 || anyCrit) status = "problem";
  else if (anyWarn) status = "warning";
  else status = "ok";

  return {
    status,
    pools_total: snap.pools.length,
    pools_degraded: degraded,
    bytes_total: totalSize,
    bytes_used: totalAlloc,
    pct_used: pctUsed,
    busiest_pool: busiest,
  };
}

// Die gerechnete Summary -- erst, wenn Pools UND Disable-Stand da sind.
function frisch(): StorageSummary | undefined {
  const d = getStorageDetails();
  if (!d) return undefined;
  // Ohne Stilllegungen zaehlte ein stillgelegter Pool kurz als degraded:
  // solange gilt die gesicherte Karte. Gibt es keine (erster Start) und
  // scheitert der Abruf der Stilllegungen, wird ohne sie gerechnet -- wie
  // frueher -- statt die Karte leer zu lassen.
  if (!entityDisablesLoaded() && _kalt) return undefined;
  return berechne(d, getStorageMonitors() ?? []);
}

export function startStorageSummary(): void {
  if (_started) return;
  _started = true;
  _kalt = loadPersisted<StorageSummary>(STORAGE_KEY);
  // Die Daten holt und haelt der Detail-Store (REST einmal, danach Protokoll).
  startStorageDetails();
  _unsub = beiStorageGesichert(() => {
    // Nur mit Stilllegungen sichern: die Karte fuer den Kaltstart soll
    // keinen stillgelegten Pool als degraded zeigen.
    const s = entityDisablesLoaded() ? frisch() : undefined;
    if (s) persist(STORAGE_KEY, s);
  });
}

export function stopStorageSummary(): void {
  _started = false;
  _kalt = undefined;
  _unsub?.();
  _unsub = undefined;
}

export function getStorageSummary(): StorageSummary | undefined {
  return frisch() ?? _kalt;
}
